import { useState } from 'react';
import { useSeoMeta, useHead } from '@unhead/react';
import {CheckCircle, AlertCircle, Loader2, Send} from 'lucide-react';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import quoteBg from '../../images/VIP House Movers Removal Boxes.jpg';
import quoteImg1 from '../../images/VIP House Movers Team Having Laugh.jpg';
import quoteImg2 from '../../images/VIP House Movers Removal Boxes.jpg';

// Web3Forms — free, no backend needed. Get your free access key at https://web3forms.com
// Replace the key below with your own from web3forms.com (sign up with info@viphousemovers.com)
const WEB3FORMS_KEY = 'ff8dcc3a-1d86-4e87-8503-c40947d75565';

const PROPERTY_SIZES = [
  'Studio / Bedsit',
  '1 Bedroom',
  '2 Bedrooms',
  '3 Bedrooms',
  '4 Bedrooms',
  '5+ Bedrooms',
  'Office / Commercial',
];

const SERVICES = [
  'House Removals',
  'Office Removals',
  'Man and Van',
  'Packing Services',
  'Furniture Assembly',
  'Storage Solutions',
  'Long Distance Removals',
  'Same Day Moves',
  'Emergency Removals',
  'Student Moves',
  'House Clearance',
  'Piano Removals',
  'Fragile Item Moving',
];

export default function Quote() {
  useSeoMeta({
    title: 'Free Removal Quote Wolverhampton | Removals Quotes Near Me | VIP House Movers',
    description: 'Get a free, no-obligation removal quote from VIP House Movers. Serving Wolverhampton, Birmingham & West Midlands for house removals, man and van, packing and storage. Call 0121 752 7107 or request online.',
    ogTitle: 'Free Removal Quote Wolverhampton | VIP House Movers',
    ogDescription: 'Request a free removal quote from VIP House Movers. Wolverhampton & West Midlands eco-friendly, insured removals.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/quote',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Free Removal Quote | VIP House Movers Wolverhampton',
    twitterDescription: 'Get a free removal quote from VIP House Movers Wolverhampton.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/quote');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'free removal quote Wolverhampton, removals quote online, house moving quote, man and van quote, packing service quote, local moving quote, removals estimate',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Quote',
              item: 'https://www.viphousemovers.com/quote',
            },
          ],
        }),
      },
    ],
  });

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    fromPostcode: '',
    toPostcode: '',
    propertySize: '3 Bedrooms',
    preferredDate: '',
    service: 'House Removals',
    additionalInfo: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const inputClass = (field: string) =>
    `w-full px-4 py-3 border rounded-lg outline-none transition-colors text-sm focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37] ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
    }`;

  function validate() {
    const e: Record<string, string> = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.phone.trim()) e.phone = 'Required';
    if (!form.fromPostcode.trim()) e.fromPostcode = 'Required';
    if (!form.toPostcode.trim()) e.toPostcode = 'Required';
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New Quote Request from ${form.firstName} ${form.lastName} — VIP House Movers`,
          from_name: 'VIP House Movers Website',
          replyto: form.email,
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
          phone: form.phone,
          service: form.service,
          property_size: form.propertySize,
          moving_from: form.fromPostcode,
          moving_to: form.toPostcode,
          preferred_date: form.preferredDate || 'Not specified',
          additional_info: form.additionalInfo || 'None',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Something went wrong.');
      setStatus('success');
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(
        err instanceof Error
          ? err.message
          : 'Unable to send your quote request. Please call us on 0121 752 7107.'
      );
    }
  }

  return (
    <SiteShell activePath="/quote" fixedNav>
      <PageHero
        eyebrow="Get a Free Quote"
        title="Tell us about your move"
        description="Share a few details and we'll prepare a transparent, tailored estimate for your VIP House Movers relocation."
        align="center"
        primaryLabel="Call 0121 752 7107"
        primaryTo="/contact"
        secondaryLabel="Contact VIP House Movers"
        secondaryTo="/contact"
        compact
        backgroundImage={quoteBg}
      />

      <div className="max-w-6xl mx-auto px-4 py-16 grid lg:grid-cols-5 gap-10">

        {/* ── Quote Form ──────────────────────────────────────────────── */}
        <div className="lg:col-span-3">
          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-gray-100">

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Quote Request Sent!</h3>
                <p className="text-gray-600 max-w-sm text-base leading-relaxed">
                  Thank you, <strong>{form.firstName}</strong>! We've emailed a confirmation to <strong>{form.email}</strong> and our team will be in touch with your tailored quote shortly — usually within 30 minutes.
                </p>
                <a
                  href="tel:01217527107"
                  className="mt-2 inline-flex items-center gap-2 bg-[#D4AF37] text-gray-900 font-bold px-6 py-3 rounded-xl hover:bg-yellow-500 transition-colors"
                >
                  📞 Call 0121 752 7107
                </a>
                <button
                  onClick={() => setStatus('idle')}
                  className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Your Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                      <input type="text" value={form.firstName} onChange={set('firstName')} className={inputClass('firstName')} placeholder="John" autoComplete="given-name" />
                      {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                      <input type="text" value={form.lastName} onChange={set('lastName')} className={inputClass('lastName')} placeholder="Doe" autoComplete="family-name" />
                      {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                      <input type="email" value={form.email} onChange={set('email')} className={inputClass('email')} placeholder="john@example.com" autoComplete="email" />
                      {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                      <input type="tel" value={form.phone} onChange={set('phone')} className={inputClass('phone')} placeholder="07700 900000" autoComplete="tel" />
                      {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Move Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Moving From (Postcode) <span className="text-red-500">*</span></label>
                      <input type="text" value={form.fromPostcode} onChange={set('fromPostcode')} className={inputClass('fromPostcode')} placeholder="e.g. WV14 7LF" />
                      {errors.fromPostcode && <p className="mt-1 text-xs text-red-500">{errors.fromPostcode}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Moving To (Postcode) <span className="text-red-500">*</span></label>
                      <input type="text" value={form.toPostcode} onChange={set('toPostcode')} className={inputClass('toPostcode')} placeholder="e.g. B1 1AA" />
                      {errors.toPostcode && <p className="mt-1 text-xs text-red-500">{errors.toPostcode}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service Required</label>
                      <select value={form.service} onChange={set('service')} className={inputClass('service')}>
                        {SERVICES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Property Size</label>
                      <select value={form.propertySize} onChange={set('propertySize')} className={inputClass('propertySize')}>
                        {PROPERTY_SIZES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Move Date</label>
                      <input type="date" value={form.preferredDate} onChange={set('preferredDate')} className={inputClass('preferredDate')} />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information <span className="text-gray-400 font-normal">(optional)</span></label>
                  <textarea
                    rows={3}
                    value={form.additionalInfo}
                    onChange={set('additionalInfo')}
                    className={inputClass('additionalInfo')}
                    placeholder="e.g. piano, fragile items, access restrictions, parking notes..."
                  />
                </div>

                {status === 'error' && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{errorMsg}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-gray-900 font-bold py-4 rounded-xl hover:bg-yellow-500 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed text-base"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending your request...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Request Free Quote
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-gray-400">
                  A confirmation will be sent to your email. No obligation — 100% free.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* ── Trust sidebar ───────────────────────────────────────────── */}
        <aside className="lg:col-span-2 space-y-6">
          <figure className="overflow-hidden rounded-2xl shadow-lg border border-gray-100">
            <img
              src={quoteImg1}
              alt="VIP House Movers team loading a family's furniture into a modern removal lorry in the West Midlands"
              width={1200}
              height={900}
              loading="lazy"
              decoding="async"
              className="w-full h-72 object-cover"
            />
            <figcaption className="sr-only">VIP House Movers removals in progress</figcaption>
          </figure>
          <figure className="overflow-hidden rounded-2xl shadow-lg border border-gray-100">
            <img
              src={quoteImg2}
              alt="Happy couple holding keys to their new home after booking a VIP House Movers quote"
              width={1200}
              height={900}
              loading="lazy"
              decoding="async"
              className="w-full h-56 object-cover"
            />
          </figure>
          <div className="bg-gray-900 text-white rounded-2xl p-6 space-y-3">
            <h4 className="font-bold text-lg text-[#D4AF37]">Why request a VIP quote?</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" /> Fixed, transparent pricing — no hidden fees</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" /> Fully insured, DBS-checked Wolverhampton team</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" /> Eco-friendly packing and recycled materials</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" /> Fast response — most quotes within 30 minutes</li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#D4AF37] shrink-0" /> Confirmation email sent instantly</li>
            </ul>
          </div>
          <a
            href="tel:01217527107"
            className="flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-gray-900 font-bold py-4 rounded-xl hover:bg-yellow-500 transition-colors text-base shadow"
          >
            📞 Prefer to call? 0121 752 7107
          </a>
        </aside>
      </div>
    </SiteShell>
  );
}

